import { DeleteOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups, selectAssetGroupsByDeviceID } from 'reduxStore/assetGroups/selectors';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { Button, Col, Flex, Form, List, Modal, Row, Select, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const AddDeviceToGroupsModal = ({ children, deviceID, open, setOpen }) => {
	// ✅ Hooks
	const dispatch = useDispatch();
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingXL,
			padding,
			paddingLG,
			paddingXS,
			colorTextDescription,
			colorText,
			paddingSM
		}
	} = theme.useToken();

	// ✅ Redux State
	const assetGroups = useSelector(selectAssetGroups);
	const assignedGroups = useSelector((state) => selectAssetGroupsByDeviceID(state, deviceID));
	const { loadingAddGroupAsset, completeAddGroupAsset } = useSelector((state) => state.AssetGroups);

	// ✅ Local State
	const [selectedGroups, setSelectedGroups] = useState([]);
	const [form] = Form.useForm();

	// ✅ Close modal
	const handleCancel = useCallback(() => {
		setSelectedGroups([]);
		form.resetFields();
		setOpen(false);
	}, [form, setOpen]);

	// ✅ Reset modal state when done
	useEffect(() => {
		if (completeAddGroupAsset) {
			handleCancel();
		}
	}, [completeAddGroupAsset, handleCancel]);

	// ✅ Filter available groups (exclude already assigned + selected)
	const availableGroups = assetGroups.filter(
		(group) => !assignedGroups.some((g) => g.groupID === group.groupID) && !selectedGroups.includes(group.groupID)
	);

	// ✅ Add device to selected groups
	const handleConnectToGroups = useCallback(() => {
		if (deviceID && selectedGroups.length > 0) {
			selectedGroups.forEach((groupID) => {
				dispatch(
					assetGroupActions.addGroupAsset({
						groupID,
						assetIDs: [deviceID]
					})
				);
			});
		}
	}, [dispatch, deviceID, selectedGroups]);

	// ✅ Select a group
	const handleSelectGroup = (groupID) => {
		if (!selectedGroups.includes(groupID)) {
			setSelectedGroups([...selectedGroups, groupID]);
		}
		// ✅ Clear select value after selection
		form.setFieldsValue({ groupSelect: undefined });
	};

	// ✅ Remove selected group
	const handleRemoveGroup = (groupID) => {
		setSelectedGroups(selectedGroups.filter((id) => id !== groupID));
	};

	// ✅ Clear selected list
	const handleClearList = () => {
		setSelectedGroups([]);
	};

	return (
		<Fragment>
			<Tooltip title='Add Device to Device Group(s)' placement='top'>
				{children ? (
					children
				) : (
					<Button icon={<UsergroupAddOutlined />} onClick={() => setOpen(true)}>
						Add Device to Group
					</Button>
				)}
			</Tooltip>
			<Modal
				open={open}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				width={472}
				footer={[
					<Flex
						align='center'
						justify='flex-end'
						style={{ marginTop: marginLG }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingAddGroupAsset}>
							Cancel
						</Button>
						<Button
							onClick={handleConnectToGroups}
							type='primary'
							loading={loadingAddGroupAsset}
							disabled={selectedGroups.length === 0}
						>
							Add Devices
						</Button>
					</Flex>
				]}
				styles={{ content: { padding: paddingXL } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Add Device(s) to Device Group(s)
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription, lineHeight: '20px' }}>
							Adding a Device to a Device Group grants it all the <br />
							permissions associated with that Device Group.
						</Text>
					</Col>

					{/* ✅ Device Group Selector */}
					<Col span={24}>
						<Form form={form} layout='vertical'>
							<Row gutter={[padding, padding]}>
								<Col span={24}>
									<Text strong>Device Groups</Text>
								</Col>
								<Col span={24}>
									<Form.Item name='groupSelect' noStyle>
										<Select
											showSearch
											placeholder='Enter or Select Device Group'
											style={{ width: '100%' }}
											options={availableGroups.map((g) => ({
												label: g.name,
												value: g.groupID
											}))}
											onSelect={handleSelectGroup}
											filterOption={(input, option) =>
												option.label.toLowerCase().includes(input.toLowerCase())
											}
										/>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</Col>

					{/* ✅ Selected Groups */}
					<Col span={24}>
						<Flex
							justify='space-between'
							align='center'
							style={{
								marginBottom: paddingLG
							}}
						>
							<Text strong>Selected Device Groups</Text>
							<Button type='text' onClick={handleClearList} style={{ textDecoration: 'underline' }}>
								Clear List
							</Button>
						</Flex>

						<List
							dataSource={selectedGroups}
							loading={loadingAddGroupAsset}
							style={{ minHeight: 186 }}
							renderItem={(groupID) => {
								const group = assetGroups.find((g) => g.groupID === groupID);
								return (
									<List.Item
										style={{ marginBottom: paddingXS, padding: 0, border: 0 }}
										extra={
											<Button
												icon={<DeleteOutlined style={{ fontSize: 16 }} />}
												onClick={() => handleRemoveGroup(groupID)}
												type='text'
												style={{ color: colorText }}
											/>
										}
									>
										<List.Item.Meta
											avatar={null}
											title={
												<Text style={{ fontWeight: 400 }}>
													{group?.name || 'Unnamed Group'}
												</Text>
											}
										/>
									</List.Item>
								);
							}}
						/>
					</Col>
				</Row>
			</Modal>
		</Fragment>
	);
};

export default AddDeviceToGroupsModal;
